/**
 * The region the project's database lives in. Edge functions pinned here talk
 * to Postgres in-region instead of across a continent.
 */
export const FUNCTION_REGION = "us-east-1";

/**
 * Pin an edge-function request to the database's region.
 *
 * Supabase runs an edge function in the region nearest the *caller*. For a
 * user in South America that is sa-east-1, while the database is in
 * us-east-1, so every query the function makes crosses a continent, and the
 * first one also pays a fresh TLS handshake. WEXARE-MVP-1K was that:
 * telegram-link-start took 1.16s when the browser's own PostgREST calls in
 * the same trace took ~230ms. Pinned, the long hop is paid once (browser →
 * function) instead of once per query.
 *
 * Only the `forceFunctionRegion` query parameter is set. functions-js's own
 * `region` option also adds an `x-region` header, which is not in the edge
 * functions' CORS allow-list, so the browser's preflight would reject every
 * call until all of them were redeployed. The gateway honors the parameter
 * alone.
 *
 * Any URL outside `/functions/v1/` (PostgREST, storage, auth) is returned
 * unchanged, as is one that already names a region.
 */
export function pinFunctionRegion(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (!parsed.pathname.includes("/functions/v1/")) return url;
  if (parsed.searchParams.has("forceFunctionRegion")) return url;
  parsed.searchParams.set("forceFunctionRegion", FUNCTION_REGION);
  return parsed.toString();
}

/**
 * A `fetch` for the Supabase client that pins edge-function calls to the
 * database's region (see pinFunctionRegion) and passes everything else
 * through.
 *
 * `fetch` is looked up at call time, not captured, so instrumentation that
 * wraps the global later (Sentry) and test stubs still see these requests.
 */
export const regionPinnedFetch: typeof fetch = (input, init) => {
  if (typeof input === "string") return fetch(pinFunctionRegion(input), init);
  if (input instanceof URL) return fetch(pinFunctionRegion(input.href), init);
  const pinned = pinFunctionRegion(input.url);
  return fetch(pinned === input.url ? input : new Request(pinned, input), init);
};
